/**
 * Blocks the builder until a required connection exists.
 * Venue: Meet, Zoom, or Teams — phone / in person / other can continue without one.
 * Payment: a paid event needs Stripe or PayPal. Both paths return here after connect.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useBuilder } from "../state/builderContext";
import { rememberBuilderReturn } from "../state/builderReturn";
import { CONFERENCING_VENUES, INVITEE_CALL_STATUS, KIND, ROUTES, STEPS, VENUE } from "../state/constants";
import { isVenueConnected } from "../state/validation";

const VENUE_DISMISS_KEY = "ebVenueGateDismissed";

const SetupRequirements = () => {
  const { state, update } = useBuilder();
  const navigate = useNavigate();
  const [venueDismissed, setVenueDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(VENUE_DISMISS_KEY) === "1";
    } catch (e) {
      return false;
    }
  });

  if (state.meta.loading || state.meta.loadError) return null;

  const lookups = state.lookups || {};
  const needsPayment = state.meta.kind === KIND.PAID && lookups.paymentStatusLoaded && lookups.paymentConnected === false;
  const noConferencing =
    lookups.connectedVenuesLoaded && !CONFERENCING_VENUES.some((id) => isVenueConnected(id, lookups.connectedVenues));
  const needsVenue = noConferencing && !venueDismissed;
  if (!needsPayment && !needsVenue) return null;

  const goConnect = (to) => {
    rememberBuilderReturn();
    navigate(to);
  };

  const continueWithoutVideo = () => {
    try {
      sessionStorage.setItem(VENUE_DISMISS_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setVenueDismissed(true);
    const venueId = parseInt(state.type.venue_id, 10);
    if (!venueId || CONFERENCING_VENUES.includes(venueId)) {
      update(STEPS.TYPE, { venue_id: String(VENUE.PHONE), invitee_call_status: INVITEE_CALL_STATUS.ME });
    }
  };

  return (
    <div className="eb-confirm-backdrop" role="presentation">
      <div className="eb-confirm" role="alertdialog" aria-modal="true" aria-labelledby="eb-setup-req-title">
        {needsPayment ? (
          <>
            <h2 id="eb-setup-req-title">Connect a payment gateway</h2>
            <p>Connect Stripe or PayPal before continuing with this paid event. After connecting, use Back to event builder to return here.</p>
            <div>
              <button type="button" className="eb-outline-small" onClick={() => navigate(ROUTES.PICKER)}>
                Back to event types
              </button>
              <button type="button" className="eb-top-create" onClick={() => goConnect(ROUTES.PAYMENT_INTEGRATIONS)}>
                Connect a payment gateway
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="eb-setup-req-title">Connect a meeting location</h2>
            <p>
              Connect Google Meet, Zoom, or Microsoft Teams before continuing. After connecting, use Back to event builder to return here. Phone, in person, and other locations do not need an integration.
            </p>
            <div>
              <button type="button" className="eb-outline-small" onClick={continueWithoutVideo}>
                Continue with phone, in person, or other
              </button>
              <button type="button" className="eb-top-create" onClick={() => goConnect("/integrations")}>
                Connect a venue
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SetupRequirements;
